import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * ClockStatusCard — Dashboard "home" card that surfaces:
 *   - The user's current clock-in status with elapsed time.
 *   - A big one-tap toggle button (mirrors the FAB).
 *   - A second action that jumps straight to the children's register.
 *   - The room/location they last clocked in to (for context).
 *
 * Designed to be the first thing a staff member or driver sees when
 * landing on their dashboard so they never have to hunt for the clock
 * action. Pairs with `ClockInOutFAB` for the floating button on every
 * other page.
 *
 * Bug-fix context: uses `/api/clock-events/my-status` (48h window) so
 * an overtime shift that crossed midnight is still reflected as
 * "Clocked in" until the user explicitly clocks out — previously the
 * dashboard incorrectly showed them as already signed out.
 */
export function ClockStatusCard({
  rooms = [],
  onOpenRegister,
  onOpenClockTab,
  showRegisterButton = true,
}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState('');

  const refresh = useCallback(async () => {
    try {
      const s = await api.getMyClockStatus();
      setStatus(s);
      if (s?.location) setLocation((cur) => cur || s.location);
    } catch (_) { /* non-fatal */ }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000);
    return () => clearInterval(t);
  }, [refresh]);

  if (!status) {
    return (
      <div className="card" style={{ marginBottom: '1rem' }} data-testid="clock-status-card-loading">
        <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>Loading clock status…</div>
      </div>
    );
  }

  if (!status.has_profile) {
    return (
      <div
        className="card"
        style={{ marginBottom: '1rem', borderColor: '#f59e0b' }}
        data-testid="clock-status-card-no-profile"
      >
        <h3 style={{ margin: 0 }}>🕒 Clock</h3>
        <p style={{ margin: '0.5rem 0 0 0', color: '#92400e' }}>
          You don't have a staff profile yet. Ask an admin to create one
          so you can clock in/out.
        </p>
      </div>
    );
  }

  const clockedIn = !!status.clocked_in;
  const hours = status.hours_since != null ? Math.round(status.hours_since * 10) / 10 : null;
  const sinceLabel = status.last_event_at
    ? new Date(status.last_event_at).toLocaleString(undefined, {
        weekday: 'short', hour: '2-digit', minute: '2-digit',
      })
    : null;

  const longShiftWarning = clockedIn && (status.hours_since || 0) > 12;

  const handleToggle = async () => {
    if (loading) return;

    if (clockedIn && (status.hours_since || 0) > 18) {
      const lastAt = sinceLabel || 'a long time ago';
      const proceed = window.confirm(
        `You appear to have been clocked in since ${lastAt} ` +
        `(~${Math.floor(status.hours_since)}h ago).\n\n` +
        `Clock out now?\n\nIf this is wrong, cancel and ask your manager ` +
        `to correct the original entry via Timesheets.`
      );
      if (!proceed) return;
    }

    setLoading(true);
    try {
      await api.createClockEvent({
        staff_id: status.staff_id,
        event_type: clockedIn ? 'clock_out' : 'clock_in',
        location: location || status.location || (rooms[0]?._id ?? ''),
      });
      toast.success(clockedIn ? 'Clocked out ✅' : 'Clocked in ✅');
      await refresh();
    } catch (err) {
      toast.error(err?.message || 'Clock action failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="card"
      style={{
        marginBottom: '1rem',
        background: clockedIn
          ? 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(16,185,129,0.02))'
          : 'linear-gradient(135deg, rgba(0,188,212,0.08), rgba(0,188,212,0.02))',
        border: `1px solid ${clockedIn ? '#10b981' : '#00BCD4'}`,
      }}
      data-testid="clock-status-card"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{
              width: '10px', height: '10px', borderRadius: '50%',
              background: clockedIn ? '#10b981' : '#9ca3af',
              boxShadow: clockedIn ? '0 0 0 4px rgba(16,185,129,0.2)' : 'none',
            }} />
            <h3 style={{ margin: 0 }}>
              {clockedIn ? 'You are clocked in' : 'You are clocked out'}
            </h3>
          </div>
          {clockedIn && (
            <div style={{ marginTop: '0.4rem', color: '#374151', fontSize: '0.9rem' }}>
              Since <strong>{sinceLabel}</strong>
              {hours != null && <> · <strong>{hours}h</strong> so far</>}
              {status.on_break && <> · <span style={{ color: '#f59e0b' }}>☕ on break</span></>}
            </div>
          )}
          {longShiftWarning && (
            <div
              style={{
                marginTop: '0.5rem',
                padding: '0.4rem 0.6rem',
                background: '#fef3c7',
                color: '#92400e',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: 600,
              }}
              data-testid="clock-long-shift-warning"
            >
              ⚠️ Long shift — remember to clock out when you finish.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {!clockedIn && rooms && rooms.length > 0 && (
            <select
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              data-testid="clock-status-card-room"
              style={{
                padding: '0.45rem 0.6rem',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '0.85rem',
              }}
            >
              <option value="">Select room…</option>
              {rooms.map((r) => (
                <option key={r._id} value={r._id}>{r.name}</option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={handleToggle}
            disabled={loading}
            data-testid="clock-status-card-toggle"
            style={{
              padding: '0.6rem 1.2rem',
              background: clockedIn ? '#ef4444' : '#10b981',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '0.95rem',
              cursor: loading ? 'wait' : 'pointer',
            }}
          >
            {loading ? '…' : clockedIn ? '🛑 Clock out' : '✅ Clock in'}
          </button>
          {typeof onOpenClockTab === 'function' && (
            <button
              type="button"
              onClick={onOpenClockTab}
              data-testid="clock-status-card-open-tab"
              style={ghostBtn}
            >
              View timesheet
            </button>
          )}
          {showRegisterButton && typeof onOpenRegister === 'function' && (
            <button
              type="button"
              onClick={onOpenRegister}
              data-testid="clock-status-card-open-register"
              style={ghostBtn}
            >
              📋 Open register
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const ghostBtn = {
  padding: '0.55rem 0.9rem',
  background: '#fff',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '0.85rem',
  cursor: 'pointer',
};

export default ClockStatusCard;
